import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppConfig } from '../../config/appConfig';
import { RequestState } from '../../enums/requestState';
import { RootState } from '../../store/store';

export interface ConfigLoaderState {
  readonly requestStates: {
    readonly fetchConfig: RequestState;
  };
  appConfig: AppConfig;
}

const initialConfigLoaderState: ConfigLoaderState = {
  requestStates: {
    fetchConfig: RequestState.IDLE,
  },
  appConfig: {} as AppConfig,
};

const configLoaderSlice = createSlice({
  name: 'configLoader',
  initialState: initialConfigLoaderState,
  reducers: {
    fetchConfig(state) {
      state.requestStates.fetchConfig = RequestState.PENDING;
    },
    fetchConfigSuccess(state, { payload }: PayloadAction<{ appConfig: AppConfig }>) {
      state.requestStates.fetchConfig = RequestState.RESOLVED;
      state.appConfig = payload.appConfig;
    },
    fetchConfigError(state, _: PayloadAction<void>) {
      state.requestStates.fetchConfig = RequestState.REJECTED;
    },
  },
});

export const configLoaderActions = configLoaderSlice.actions;
export const configLoaderReducers = configLoaderSlice.reducer;
export const selectConfigLoaderState = (state: RootState) => state.configLoader;
export const selectAppConfig = (state: RootState) => selectConfigLoaderState(state).appConfig;
export const selectBaseApiUrl = (state: RootState) => selectConfigLoaderState(state).appConfig.baseApiUrl;
export const selectEnvironment = (state: RootState) => selectConfigLoaderState(state).appConfig.environment;
export const selectCompany = (state: RootState) => selectConfigLoaderState(state).appConfig.vendor;
export const selectFirebaseApiKey = (state: RootState) => selectConfigLoaderState(state).appConfig.firebase.apiKey;
export const selectLoginUrl = (state: RootState) => selectConfigLoaderState(state).appConfig.loginUrl;
export const selectCookieAuth = (state: RootState) => selectConfigLoaderState(state).appConfig.cookieAuth === true;
export const selectIsMicrosoftLoginEnabled = (state: RootState) =>
  selectConfigLoaderState(state).appConfig.loginOptions.isMicrosoftLoginEnabled;
export const selectIsEmailLoginEnabled = (state: RootState) => selectConfigLoaderState(state).appConfig.loginOptions.isEmailLoginEnabled;
export const selectIsGoogleLoginEnabled = (state: RootState) => selectConfigLoaderState(state).appConfig.loginOptions.isGoogleLoginEnabled;
export const selectIsAppConfigLoading = (state: RootState) =>
  selectConfigLoaderState(state).requestStates.fetchConfig === RequestState.PENDING;

export const selectIsAppConfigResolved = (state: RootState) =>
  selectConfigLoaderState(state).requestStates.fetchConfig === RequestState.PENDING;

export const selectIsAnyOtherLoginOptionEnabled = createSelector(
  [selectIsMicrosoftLoginEnabled, selectIsGoogleLoginEnabled],
  (isMicrosoftLoginEnabled, isGoogleLoginEnabled) => isMicrosoftLoginEnabled || isGoogleLoginEnabled
);

export const selectIsAppConfigAvailable = createSelector([selectAppConfig], (appConfig) => {
  // Check if appConfig is defined and has the expected properties
  return appConfig && Object.keys(appConfig).length > 0;
});
